import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { useQueryManager } from "@/composables/useQueryManager";
import type { StepConfig } from "@/composables/useStepFunctions";
import { useSignupStore } from "@/signup/signup";
import type { GeneratorUploadResult } from "@/types";
import useVuelidate, { type Validation } from "@vuelidate/core";
import type { Ref } from "vue";

/**
 * How a signup flow's steps save: the shown step is validated, the draft saved, and the ids the
 * server assigned go into the URL, so the next save names the company and generator it created.
 *
 * Call it in the flow's setup. Without `v$` it validates the flow's own child steps: a bare
 * `useVuelidate()` collects the validations of the components under the instance that calls it.
 */
export function useSignupStepSave(
  isLoading: Ref<boolean>,
  v$: Ref<Validation> = useVuelidate()
) {
  const signupStore = useSignupStore();
  const { updateQuery } = useQueryManager();
  const { globalErrorHandler } = useGlobalHandlers();

  /** A step's `postStepCallback`: false keeps the flow on the step, and a failed save is reported. */
  async function postStepCallback({
    apiEndpoint,
  }: StepConfig): Promise<boolean> {
    try {
      v$.value.$touch();
      if (v$.value.$error) return false;
      if (apiEndpoint) await saveStep(apiEndpoint);
      return true;
    } catch (error) {
      globalErrorHandler(error);
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  /** Takes the answer of a file upload, which posts its own request, into both stores and the URL. */
  async function takeUpload({
    accountData,
    company_id,
    generator_id,
  }: GeneratorUploadResult): Promise<void> {
    signupStore.takeAnswer(accountData);
    await updateQuery({
      generator_id: String(generator_id),
      company_id: String(company_id),
    });
  }

  async function saveStep(url: string): Promise<void> {
    const { company_id, generator_id } = await signupStore.saveDraft(url);
    // Lowered before the URL push as well as in `finally`, so the overlay is down while it lands.
    isLoading.value = false;
    await updateQuery({
      company_id: String(company_id),
      ...(generator_id && { generator_id: String(generator_id) }),
    });
  }

  return { postStepCallback, takeUpload };
}
